import { useMemo } from "react";
import type { Selection, Key } from "react-aria-components";
import { useMap } from "react-map-gl/mapbox";
import { getUniqueId } from "@medoma/models/activities";
import { patientStatusSchema } from "@medoma/models/patients";
import { selectedItemTypeSchema, useSelection } from "./SelectionContext";
import type { IUnplannedOccurrenceOrGroup } from "@medoma/models/activities";

type UseSelectionInSidebarParams = {
  unplannedOccurrencesAndGroups: IUnplannedOccurrenceOrGroup[] | undefined;
};

export const useSelectionInSidebar = ({
  unplannedOccurrencesAndGroups,
}: UseSelectionInSidebarParams) => {
  const {
    selectedItem,
    setSelectedItem,
    clearSelection,
    boundsShowingAllPatients,
  } = useSelection();

  const { planningMap } = useMap();

  const getOccurrenceOrGroupByFirstKey = (keys: Selection) => {
    if (!keys) {
      return undefined;
    }
    if (keys === "all") {
      return undefined;
    }

    const firstKey = keys.values().next().value;

    return unplannedOccurrencesAndGroups?.find(
      (item) => getUniqueId(item) === firstKey,
    );
  };

  const getPatientCoordinates = (newSelectedKeys: Selection) => {
    const matchedItem = unplannedOccurrencesAndGroups?.find(
      (unplannedOccurrenceOrGroup) => {
        return (
          newSelectedKeys !== "all" &&
          newSelectedKeys.has(getUniqueId(unplannedOccurrenceOrGroup))
        );
      },
    );

    if (
      !matchedItem ||
      !matchedItem.patient ||
      matchedItem.patient.status === patientStatusSchema.enum.deleted
    ) {
      return null;
    }

    const { longitude, latitude } = matchedItem.patient.address.coordinates;
    if (longitude && latitude) {
      return { longitude, latitude };
    }
    return null;
  };

  const easeToPatientCoordinates = (newSelectedKeys: Selection) => {
    if (newSelectedKeys === "all" || newSelectedKeys?.size === 0) {
      return;
    }

    const patientCoordinates = getPatientCoordinates(newSelectedKeys);

    if (patientCoordinates) {
      planningMap?.easeTo({
        center: [patientCoordinates.longitude, patientCoordinates.latitude],
        duration: 1000,
      });
    }
  };

  const easeToBounds = () => {
    if (!planningMap) {
      return;
    }
    if (!boundsShowingAllPatients) {
      return;
    }

    const center = {
      lat:
        (boundsShowingAllPatients[0].lat + boundsShowingAllPatients[1].lat) / 2,
      lng:
        (boundsShowingAllPatients[0].lng + boundsShowingAllPatients[1].lng) / 2,
    };

    planningMap.easeTo({
      center,
      duration: 1000,
    });
  };

  const handleSelectionChange = (newSelectedKeys: Selection | null) => {
    if (
      newSelectedKeys === "all" ||
      (newSelectedKeys && newSelectedKeys.size > 0)
    ) {
      easeToPatientCoordinates(newSelectedKeys);

      const firstOccurrenceOrGroup =
        getOccurrenceOrGroupByFirstKey(newSelectedKeys);
      if (firstOccurrenceOrGroup) {
        setSelectedItem({
          item: firstOccurrenceOrGroup,
          type: selectedItemTypeSchema.enum.itemToPlan,
        });
      }
    } else {
      easeToBounds();
      clearSelection();
    }
  };

  const selectedKeys: Set<Key> = useMemo(() => {
    if (!selectedItem) {
      return new Set();
    }
    switch (selectedItem.type) {
      case selectedItemTypeSchema.enum.itemToPlan:
      case selectedItemTypeSchema.enum.plannedActivityOccurrenceOrGroup:
        return new Set([getUniqueId(selectedItem.item)]);
      case selectedItemTypeSchema.enum.plannedVisit:
        return new Set([selectedItem.item.id]);
      case selectedItemTypeSchema.enum.plannedRoute:
        return new Set();
      default:
        throw selectedItem satisfies never;
    }
  }, [selectedItem]);

  return {
    handleSelectionChange,
    selectedKeys,
  };
};
